import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { ModalForm } from '../../../components/form-components/modal-form.tsx';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { SalesOrderSearchRow } from '../../../server/types/search-types.ts';
import { SearchEntity, SearchEntityRoute } from '../../../server/types/search-types.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { EntitySearchCombobox } from '../../shared/components/form-items-with-fetch/entity-search-combobox.tsx';
import { UsersSelect } from '../../shared/components/form-items-with-fetch/users-select.tsx';
import { useSearchEntityNavigation } from '../../shared/hooks/use-search-entity.ts';

import { ArrowUpRight, CalendarIcon, CircleUserRoundIcon, UserIcon } from 'lucide-react';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { getDueDateColor } from '../../../utils/task-helpers.ts';

type TaskFormFieldsProps = {
    form: UseFormReturn<CrmTaskInputDTO>;
    task?: CrmTaskDTO;
};

export const TaskFormFields = ({ form, task }: TaskFormFieldsProps) => {
    const { t } = useLocale('tasks');
    const { pathname } = useLocation();

    const showGoToCustomerDetail = !pathname.includes('/customers/');

    const {
        control,
        watch,
        formState: { errors: formErrors },
    } = form;

    const watchedLinks = watch('links');

    const watchedLink =
        watchedLinks.find((link) => link.modelType !== (SearchEntity.Customer as string)) ||
        watchedLinks.find((link) => link.modelType === (SearchEntity.Customer as string));

    const { navigateToEntity } = useSearchEntityNavigation();

    return (
        <DialogForm.ItemList subgrid>
            <DialogForm.Item>
                <DialogForm.Label icon={ArrowUpRight}>
                    {t('related_to', {
                        ns: 'common',
                    })}
                </DialogForm.Label>
                <DialogForm.Body className="flex-col">
                    <Controller
                        name="links"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            const values = value;
                            const salesOrderTypes = [SearchEntity.SalesOrder, SearchEntity.SalesQuote];

                            const salesOrderValue = values.find((value) =>
                                salesOrderTypes.includes(value.modelType as SearchEntity),
                            );

                            const defaultValue = (salesOrderValue ? [salesOrderValue] : values).map((v) => {
                                const entity = v.modelType as SearchEntity;

                                return {
                                    id: v.modelId,
                                    entity,
                                    name: v.title || '',
                                };
                            });

                            return (
                                <EntitySearchCombobox
                                    entities={[
                                        SearchEntity.Customer,
                                        SearchEntity.SalesOrder,
                                        SearchEntity.SalesQuote,
                                    ]}
                                    inputPlaceholder={t('tasks__entity_search__placeholder')}
                                    className="w-full"
                                    variant="inline"
                                    tracking={{
                                        context: 'task_linked_entity_select',
                                        context_entity_type: 'task',
                                        context_entity_id: task?.taskId,
                                    }}
                                    onValueChange={(values) => {
                                        const [item] = values;

                                        if (item) {
                                            const newLinks = [
                                                {
                                                    modelId: item.id,
                                                    modelType: item.entity,
                                                    title: item.name,
                                                },
                                            ];

                                            if (salesOrderTypes.includes(item.entity)) {
                                                const searchRow = item.original as SalesOrderSearchRow;

                                                newLinks.push({
                                                    modelId: searchRow.customer_id,
                                                    modelType: SearchEntity.Customer,
                                                    title: searchRow.customer_name || '',
                                                });
                                            }

                                            onChange(newLinks);
                                        } else {
                                            onChange([]);
                                        }
                                    }}
                                    defaultValue={defaultValue}
                                />
                            );
                        }}
                    />
                    {watchedLink && showGoToCustomerDetail && (
                        <Link
                            onClick={(e) => {
                                e.preventDefault();

                                navigateToEntity({
                                    entity: watchedLink.modelType as SearchEntity,
                                    erp_id: watchedLink.modelId,
                                });

                                return null;
                            }}
                            to={`${SearchEntityRoute[watchedLink.modelType as keyof typeof SearchEntityRoute]}/${watchedLink.modelId}`}
                            className="text-sm font-medium text-blue-500 px-3"
                        >
                            {t('show_details', { ns: 'common' })}
                        </Link>
                    )}
                </DialogForm.Body>
                <ModalForm.Error errorMessage={formErrors.links} />
            </DialogForm.Item>

            <DialogForm.Item>
                <DialogForm.Label htmlFor="dueDate" icon={CalendarIcon}>
                    {t('due_date', {
                        ns: 'common',
                    })}
                </DialogForm.Label>
                <DialogForm.Body>
                    <Controller
                        name="dueDate"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            return (
                                <DatePicker
                                    disableIcon
                                    mode="single"
                                    value={value}
                                    onChange={onChange}
                                    textColor={
                                        task?.status === 'COMPLETED'
                                            ? 'neutral-700'
                                            : getDueDateColor({ dueDate: value })
                                    }
                                    disabled={{
                                        before: new Date(),
                                    }}
                                />
                            );
                        }}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={formErrors.dueDate} />
            </DialogForm.Item>
            <DialogForm.Item>
                <DialogForm.Label htmlFor="assignedToId" icon={CircleUserRoundIcon}>
                    {t('assigned_to', {
                        ns: 'common',
                    })}
                </DialogForm.Label>
                <DialogForm.Body>
                    <Controller
                        name="assignedToId"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <UsersSelect
                                value={value}
                                onValueChange={onChange}
                                className="w-full justify-start"
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={formErrors.assignedToId} />
            </DialogForm.Item>
            {task && (
                <DialogForm.Item>
                    <DialogForm.Label icon={UserIcon}>{t('tasks__form__created_by_label')}</DialogForm.Label>
                    <DialogForm.Body className="items-center">
                        <Text color="neutral-600" className="px-3">
                            {task.createdBy.firstName} {task.createdBy.lastName}
                        </Text>
                    </DialogForm.Body>
                </DialogForm.Item>
            )}
        </DialogForm.ItemList>
    );
};
